import { z } from 'zod'
import {
  DemandDetailsSchema,
  EstimatedPerformanceSummarySchema,
  LaunchAndGrowthKPIsResponse,
  MonthlySupplyDemandDelta,
} from '../token-designer'

export const MetaSchema = z.object({
  xAxis: z.string(),
  labels: z.record(z.string(), z.any()),
})

export const UnlockProgressGroupResponse = z.object({
  group_name: z.string(),
  total_allocation: z.number().safe(),
  unlocked_percentage: z.number().safe(),
  allocation_percentage: z.number().safe(),
  tokens_unlocked: z.number().safe(),
  tokens_locked: z.number().safe(),
})

export const UnlockProgressResponse = z.object({
  groups_table: z.array(UnlockProgressGroupResponse),
  cumulative_monthly_unlock: z.object({
    data: z.array(
      z.record(z.union([z.number().safe(), z.string()])).and(
        z.object({
          date: z.string(),
          total_tokens_unlocked: z.number().safe(),
          percentage_of_total_unlocked: z.number().safe(),
        }),
      ),
    ),
    meta: MetaSchema,
  }),
  current_unlock_progress: z.number().safe(),
  total_tokens: z.number().safe(),
  total_tokens_locked: z.number().safe(),
  total_value_locked: z.number().safe(),
  total_tokens_unlocked: z.number().safe(),
  total_value_unlocked: z.number().safe(),
  days_left: z.number().safe(),
  months_left: z.number().safe(),
})

export const ChartDataItemSchema = z.record(z.union([z.string(), z.number().safe()]))

export const UpcomingTokenUnlocksResponse = z.object({
  total_tokens_to_be_unlocked: z.number().safe(),
  percent_of_total_supply: z.number().safe(),
  percent_of_circulating_supply: z.number().safe(),
  total_value_to_be_unlocked: z.number().safe(),
  unlocked_by_group: z.object({
    data: z.array(ChartDataItemSchema),
    meta: MetaSchema,
  }),
  cumulative_value_unlocked_groups: z.object({
    data: z.array(ChartDataItemSchema),
    meta: MetaSchema,
  }),
})

export const TotalTokensUnlockedResponse = z.object({
  total_tokens_unlocked: z.number().safe(),
})

export const SupplyDemandTypeResponse = z.object({
  message: z.string(),
  result: z.array(MonthlySupplyDemandDelta),
})

export const CumulativeEmissionResponse = z
  .object({
    month: z.number().safe(),
  })
  .catchall(z.number().safe())

export const CalculatorEmissionResponse = z.object({
  averageAnnualInflation: z.number().safe(),
  circulatingSupplyAtTGE: z.number().safe(),
  cumulativeEmission: z.array(CumulativeEmissionResponse),
  percentageUnlockedAtTGE: z.number().safe(),
  totalEmissionDuration: z.number().safe(),
  total_groups: z.number().safe(),
})

export const CalculatorEmissionsResponse = z.object({
  emissions: CalculatorEmissionResponse,
  message: z.string().optional(),
})

export const GetLaunchAndGrowthKpisResponse = z.object({
  result: LaunchAndGrowthKPIsResponse,
})

export const DailyPriceSchema = z.object({
  'Buy Pressure (Qty)': z.number().safe(),
  'Buy Pressure (US Dollar Value) - Adjusted by Elasticity': z.number().safe(),
  'Buy Pressure (US Dollar Value) - Unadjusted by Elasticity': z.number().safe(),
  'Buy Pressure (US Dollars)': z.number().safe(),
  'Days post-TGE': z.number().safe(),
  'Demand Elasticity Adjustment': z.number().safe(),
  'Ending Fully Dilluted Valuation': z.number().safe(),
  'Ending Market Capitalization': z.number().safe(),
  'Ending Price': z.number().safe(),
  'Impact on Previous Price': z.number().safe(),
  'Liquidity as a % of Market Capitalization': z.number().safe(),
  'Months post-TGE': z.number().safe(),
  'Net Buy / Sell Pressure (US Dollar Value)': z.number().safe(),
  'Price % Change vs. Post-TGE Pop': z.number().safe(),
  'Price % Change vs. TGE': z.number().safe(),
  'Sell Pressure (Qty)': z.number().safe(),
  'Sell Pressure (US Dollar Value)': z.number().safe(),
  'Slippage Induced by Trade Simulation': z.number().safe(),
  'Starting Fully Dilluted Valuation': z.number().safe(),
  'Starting Market Capitalization': z.number().safe(),
  'Starting Price': z.number().safe(),
  'US Dollar Value of Bids & Offers per 1% relative to Midpoint': z.number().safe(),
})

export const MonthlySupplyGrowthSchema = z.object({
  mon_supply_growth: z.number().safe(),
  month: z.number().safe(),
})

export const MonthlyCumulativeSellPressureSchema = z.record(z.number().safe())

export const MonthlyDollarDemandSchema = z.object({
  demand_drivers: z.record(z.number().safe()),
  months_post_tge: z.number().safe(),
})

export const DailyPriceResponse = z.object({
  daily_price: z.array(DailyPriceSchema),
  demand_details: DemandDetailsSchema,
  message: z.string().optional(),
  monthly_circulating_supply_growth: z.array(MonthlySupplyGrowthSchema),
  monthly_cumulative_sell_pressure: z.array(MonthlyCumulativeSellPressureSchema),
  monthly_dollar_demand: z.array(MonthlyDollarDemandSchema),
  estimated_performance_summary: z.array(EstimatedPerformanceSummarySchema),
})

export type UnlockProgress = z.infer<typeof UnlockProgressResponse>
export type UpcomingTokenUnlocks = z.infer<typeof UpcomingTokenUnlocksResponse>
export type SupplyDemandType = z.infer<typeof SupplyDemandTypeResponse>
export type CalculatorEmissionResponseType = z.infer<typeof CalculatorEmissionResponse>
export type CalculatorEmissionsResponseType = z.infer<typeof CalculatorEmissionsResponse>
export type MonthlySupplyGrowth = z.infer<typeof MonthlySupplyGrowthSchema>
export type MonthlyCumulativeSellPressure = z.infer<typeof MonthlyCumulativeSellPressureSchema>
export type DailyPriceResponseType = z.infer<typeof DailyPriceResponse>
export type DailyPrice = z.infer<typeof DailyPriceSchema>
